import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  alpha,
} from '@mui/material';
import toast from 'react-hot-toast';
import axios from 'axios';

interface PasswordDialogProps {
  open: boolean;
  onClose: () => void;
  colors: any;
  onPasswordVerified: () => void; // Add this prop
}

const PasswordDialog: React.FC<PasswordDialogProps> = ({
  open,
  onClose,
  colors,
  onPasswordVerified, // Add this
}) => {
  const [passwordInput, setPasswordInput] = useState('');
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);

  const modalStyle = {
    borderRadius: '24px',
    bgcolor: '#0F1A15', 
    border: `1px solid ${alpha(colors.accent, 0.3)}`,
    boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
    color: '#FFFFFF'
  };

  const handleVerifyPassword = async () => {
    if (!passwordInput.trim()) {
      toast.error("Please enter your password");
      return;
    }

    setIsVerifyingPassword(true);
    try {
      // Call your API to verify password
      const response = await axios.post('/api/auth/verify-password', {
        password: passwordInput
      });
      
      if (response.data.success) {
        // Call the callback to update parent state
        onPasswordVerified();
        onClose();
        setPasswordInput('');
        toast.success("Identity verified successfully.");
      } else {
        toast.error("Incorrect password. Please try again.");
      }
    } catch (error: any) {
      console.error('Password verification error:', error);
      
      // For development/testing, use a mock verification
      // REMOVE THIS IN PRODUCTION - use actual API call
      if (passwordInput === "password123" || passwordInput.length > 3) {
        onPasswordVerified();
        onClose();
        setPasswordInput('');
        toast.success("Identity verified successfully.");
      } else {
        toast.error(error.response?.data?.error || "Failed to verify password.");
      }
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: modalStyle }}>
      <DialogTitle sx={{ fontWeight: 700, color: '#FFFFFF' }}>
        Confirm Password
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
          Please enter your password to reveal your email address.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          type="password"
          variant="filled"
          label="Password"
          value={passwordInput}
          onChange={(e) => setPasswordInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleVerifyPassword();
            }
          }}
          InputProps={{ 
            disableUnderline: true, 
            style: { 
              borderRadius: '12px', 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              color: '#fff' 
            } 
          }}
          InputLabelProps={{ style: { color: 'rgba(255,255,255,0.5)' } }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} sx={{ color: '#fff', fontWeight: 600, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleVerifyPassword} 
          variant="contained" 
          disabled={isVerifyingPassword || !passwordInput.trim()}
          sx={{ 
            bgcolor: colors.accent, 
            color: '#fff', 
            fontWeight: 600, 
            borderRadius: '20px', 
            textTransform: 'none', 
            px: 3, 
            boxShadow: 'none',
            '&:hover': {
              bgcolor: alpha(colors.accent, 0.8),
            }
          }}
        >
          {isVerifyingPassword ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordDialog;